// The tone picker + editable draft + send-from-your-own-account controls,
// shared by BOTH Manager drafting surfaces:
//
//   - `ManagerMaeveFirstResponse` — Sub-mode A, scoped to a support pathway,
//     which additionally stamps `first_response_sent_at` when the manager
//     commits (that stamp is what short-circuits the §6.4 24h non-response
//     Amber alert, so it is safety-relevant, not cosmetic).
//   - `ManagerDraftResponse` — Sub-mode A′, case-less (PO 2026-08-06), which
//     has no case to stamp and so commits to nothing.
//
// Extracted rather than copied because the parts that are identical are the
// parts that are easy to get subtly wrong twice: clipboard-first ordering (a
// network failure must still leave the manager holding the text), the
// `window.prompt` fallback for restrictive clipboard contexts, the per-tone
// edit buffer that survives tone switches, and the mailto subject that changes
// with tone. The ONLY difference between the two callers is `onCommit`.
//
// Presentational: it owns tone selection and the edit buffer, and nothing
// else. It never calls an API — the caller supplies both the variants and
// whatever committing means on its surface.

import { useState, type ReactNode } from 'react';
import type { FirstResponseVariant, ToneVariant } from '@besideu/shared/maeve';
import { Button } from './ui/Button.tsx';

const TONE_ORDER = ['warm', 'formal', 'gentle'] as const;

const TONE_LABEL: Record<ToneVariant, string> = {
  warm: 'Warm',
  formal: 'Formal',
  gentle: 'Gentle',
};

const TONE_BLURB: Record<ToneVariant, string> = {
  warm: 'Personal and present. Best when you already have a close working relationship.',
  formal: 'Structured and professional. Best in formal cultures or for newer relationships.',
  gentle: 'Soft, low-pressure. Best when the employee may need space to breathe first.',
};

export function DraftToneComposer({
  variants,
  onCommit,
  commitPending = false,
  commitError,
  confirmLabel,
  confirmTitle,
  footnote,
}: {
  variants: FirstResponseVariant[];
  /**
   * Fired after the manager sends via any channel, or presses the confirm
   * button, with the tone they chose and their final edited text. The
   * pathway-scoped caller passes the `first_response_sent_at` stamp; the
   * case-less caller omits it entirely, because there is no case to stamp
   * and inventing one would be worse than recording nothing.
   */
  onCommit?: (tone: ToneVariant, text: string) => void;
  commitPending?: boolean;
  /** Caller-rendered error surface for whatever `onCommit` does. */
  commitError?: ReactNode;
  /**
   * Explicit "I'm using this one" action. Omitted when committing is a no-op
   * — a button whose only effect is a clipboard copy already exists ("Copy
   * for chat"), so a second one would be two buttons for one outcome.
   */
  confirmLabel?: string;
  confirmTitle?: string;
  /** Replaces the default privacy note under the draft box. */
  footnote?: ReactNode;
}) {
  // Warm is the pre-selected default (Journey 01, STEP 2).
  const [tone, setTone] = useState<ToneVariant>('warm');
  // Per-tone edit buffer, seeded from the model's drafts. Switching tone keeps
  // any edits the manager already made to each one.
  const [edits, setEdits] = useState<Record<ToneVariant, string>>(() => {
    const seed = { warm: '', formal: '', gentle: '' } as Record<ToneVariant, string>;
    for (const v of variants) seed[v.tone] = v.text;
    return seed;
  });
  const [copied, setCopied] = useState(false);

  const text = edits[tone];
  const setText = (next: string) => setEdits((prev) => ({ ...prev, [tone]: next }));

  async function copyText(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in restrictive contexts — fall back to a prompt.
      window.prompt('Copy this draft:', text);
    }
  }

  // Primary action — copy to clipboard, then commit. Clipboard first so a
  // network failure still leaves the manager with the text.
  async function useThisDraft(): Promise<void> {
    await copyText();
    onCommit?.(tone, text);
  }

  // Send-via-channel dispatch: copy the draft first (so any failure still
  // leaves the text in hand), open the native handler, then commit.
  async function onSendVia(channel: 'email' | 'teams' | 'chat'): Promise<void> {
    await copyText();
    if (channel === 'email') {
      const subject = encodeURIComponent(
        tone === 'formal' ? 'Following up on your loss' : 'Thinking of you',
      );
      const body = encodeURIComponent(text);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } else if (channel === 'teams') {
      window.open('https://teams.microsoft.com/l/chat/0/0', '_blank', 'noopener,noreferrer');
    }
    // 'chat' — the clipboard copy above is the whole flow (paste into WhatsApp / Slack).
    onCommit?.(tone, text);
  }

  return (
    <div className="space-y-3">
      {/* Tone picker — Warm pre-selected (Journey 01 STEP 2). */}
      <div role="group" aria-label="Response tone" className="flex flex-wrap gap-2">
        {TONE_ORDER.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTone(t)}
            aria-pressed={tone === t}
            className={
              tone === t
                ? 'rounded-full bg-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground'
                : 'rounded-full border border-border px-3.5 py-1.5 text-sm text-muted-foreground transition hover:text-foreground'
            }
          >
            {TONE_LABEL[t]}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{TONE_BLURB[tone]}</p>

      {/* Draft card (Maeve 3 — the "DRAFT MESSAGE" boxed, editable draft). */}
      <div className="rounded-lg border border-primary-soft-border bg-primary-soft/40 p-4">
        <p className="mb-2 text-[0.68rem] font-semibold tracking-[0.16em] text-primary uppercase">
          Draft message
        </p>
        <label className="block">
          <span className="sr-only">Draft body ({TONE_LABEL[tone]})</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            // text-base = 16px so iOS Safari doesn't zoom on focus.
            className="block w-full resize-y rounded-md border border-border bg-card p-3 text-base"
            rows={7}
          />
        </label>
      </div>

      {commitError}

      <p className="text-xs text-muted-foreground">
        {footnote ?? (
          <>
            Edit freely, then send from your own account. The platform stores nothing of the message
            text.
          </>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => void onSendVia('email')} className="py-1.5 text-sm">
          Send via email
        </Button>
        <Button
          variant="secondary"
          onClick={() => void onSendVia('teams')}
          className="py-1.5 text-sm"
        >
          Send via Teams
        </Button>
        <Button
          variant="secondary"
          onClick={() => void onSendVia('chat')}
          className="py-1.5 text-sm"
          title="Copies the draft to your clipboard — paste into WhatsApp or Slack"
          aria-live="polite"
        >
          {copied ? 'Copied — paste in chat' : 'Copy for chat'}
        </Button>
        {confirmLabel ? (
          <Button
            variant="secondary"
            onClick={() => void useThisDraft()}
            loading={commitPending}
            disabled={text.trim().length === 0}
            className="py-1.5 text-sm"
            {...(confirmTitle ? { title: confirmTitle } : {})}
          >
            {confirmLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
